import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/hooks/useAuth';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      await login(email, password);
      // Small delay to ensure Zustand state is updated before navigation
      setTimeout(() => {
        navigate('/');
      }, 100);
    } catch {
      // Error is handled by the auth store
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4">
      <div className="login-grid-background" aria-hidden="true" />

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-ds-brand-solid flex items-center justify-center shadow-lg shadow-ds-brand/20">
            <span className="text-ds-on-brand font-bold text-2xl">AI</span>
          </div>
        </div>

        {/* Card */}
        <div className="login-grid-card rounded-2xl border border-ds-border p-8 shadow-xl">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-ds-text-primary mb-2">
              Bem-vindo de volta
            </h1>
            <p className="text-ds-text-secondary">
              Entre com suas credenciais para acessar o painel
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-ds-text-secondary mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-ds-text-secondary" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  className="pl-11"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-ds-text-secondary mb-2">
                Senha
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-ds-text-secondary" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-11 pr-11"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ds-text-secondary hover:text-ds-text-primary"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-ds-danger/10 border border-ds-danger/20 text-ds-danger text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              size="lg"
              isLoading={isLoading}
            >
              {isLoading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <a
              href="#"
              className="text-sm text-ds-text-secondary hover:text-ds-brand transition-colors"
            >
              Esqueceu sua senha?
            </a>
          </div>
        </div>

        <p className="text-center mt-8 text-sm text-ds-text-secondary">
          © 2026 AlpendreInfra. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
}

export { Login };
